/**
 * Error Log Store
 *
 * ISSUE_046: Session Conflict Prevention - Error History Storage
 *
 * Stores sync error history for UI display and debugging.
 * Maximum 50 errors retained (FIFO).
 *
 * @module useErrorLogStore
 */

import { create } from "zustand";
import { v4 as uuid } from "uuid";

// ============================================================================
// TYPES
// ============================================================================

export interface SyncError {
  /** Unique error ID */
  id: string;
  /** When the error occurred (epoch ms) */
  timestamp: number;
  /** Session ID that failed */
  sessionId: string;
  /** Student name for display */
  studentName: string;
  /** Teacher name for context */
  teacherName?: string;
  /** Time range string (e.g., "14:30~15:30") */
  time: string;
  /** Error message */
  error: string;
  /** ID of conflicting session (if applicable) */
  conflictSessionId?: string;
}

// ============================================================================
// STATE & ACTIONS
// ============================================================================

interface ErrorLogState {
  /** Error history (newest first) */
  errors: SyncError[];
}

interface ErrorLogActions {
  /** Add a new error to the log */
  addError: (error: Omit<SyncError, "id" | "timestamp">) => void;
  /** Remove a specific error by ID */
  removeError: (id: string) => void;
  /** Clear all errors */
  clearAll: () => void;
  /** Get error count */
  getErrorCount: () => number;
}

/** Maximum number of errors to retain */
const MAX_ERRORS = 200;

// ============================================================================
// STORE
// ============================================================================

export const useErrorLogStore = create<ErrorLogState & ErrorLogActions>((set, get) => ({
  errors: [],

  addError: (error) =>
    set((state) => ({
      errors: [
        { ...error, id: uuid(), timestamp: Date.now() },
        ...state.errors,
      ].slice(0, MAX_ERRORS),
    })),

  removeError: (id) =>
    set((state) => ({
      errors: state.errors.filter((e) => e.id !== id),
    })),

  clearAll: () => set({ errors: [] }),

  getErrorCount: () => get().errors.length,
}));

// ============================================================================
// SELECTORS
// ============================================================================

/** Get error count (for badge) */
export const useErrorCount = () =>
  useErrorLogStore((state) => state.errors.length);

/** Get all errors */
export const useErrors = () =>
  useErrorLogStore((state) => state.errors);

export default useErrorLogStore;
